/**
 * useDecisionReview (FLOW-577, generalised in FLOW-667)
 *
 * The confirm/dismiss flow for a queue of proposed decisions: run FLOW-573's
 * contradiction check against the saved decision first, hold the warnings it
 * raises, and apply the human's dispositions once every one has an outcome.
 *
 * Two surfaces review proposed decisions — `CapturedDecisionsPanel` for what
 * extraction pulled out of a breakdown session, and `DecisionsPage` for
 * everything else, including the `mcp_agent` rows `propose_decision` lands
 * with no intake to review them in. The orchestration below is the same on
 * both, so it lives here rather than in each: a single `useConflictCheck`
 * instance backs one flow at a time, which is why `activeId` is a single id.
 *
 * Detection enriches, it never gates: a clear or unavailable result confirms
 * straight through. Applying is one-shot (D2) — confirm the decision, then
 * the edges an acceptance implies (D3), then resolve every warning (D4:
 * consequence before measurement).
 *
 * Persisting the confirm and the dismiss stays with the caller: the two
 * surfaces hold their decisions differently (an intake's captured list vs a
 * search resource) and each patches its own.
 */

import { Accessor, createSignal } from "solid-js";
import type {
  ConflictResolution,
  DecisionConflictWarning,
  DecisionId,
  ProjectId,
} from "../api/types";
import { useConflictCheck } from "./useConflictCheck";
import { describeAiError, surfaceQuotaExhausted } from "../utils/aiError";

export interface UseDecisionReviewOptions {
  /** Persists the confirm (status → active). Resolves to whether it succeeded. */
  onConfirm: (id: DecisionId) => Promise<boolean>;
  /** Persists the dismiss. */
  onDismiss: (id: DecisionId) => void;
}

export interface UseDecisionReviewResult {
  warnings: Accessor<DecisionConflictWarning[]>;
  choices: Accessor<Record<string, ConflictResolution | undefined>>;
  /** True while the chosen dispositions are being applied */
  applying: Accessor<boolean>;
  /** Failure from applying the dispositions, if any */
  applyError: Accessor<string | null>;
  clearApplyError: () => void;
  /** Whether this decision's warnings are the ones currently on screen */
  showingWarnings: (id: DecisionId) => boolean;
  confirmDisabled: (id: DecisionId) => boolean;
  choose: (warningId: string, outcome: ConflictResolution) => void;
  /** Starts the check, or applies the dispositions if the warnings are already up */
  confirm: (id: DecisionId) => void;
  /** Leaves the review without confirming or dismissing — "not now" */
  cancel: () => void;
  dismiss: (id: DecisionId) => void;
}

export function useDecisionReview(
  projectId: Accessor<ProjectId>,
  options: UseDecisionReviewOptions,
): UseDecisionReviewResult {
  const conflictCheck = useConflictCheck(projectId);
  // The id of the decision currently running the check/warnings flow — only
  // one at a time, since a single useConflictCheck instance backs it.
  const [activeId, setActiveId] = createSignal<DecisionId | null>(null);
  const [choices, setChoices] = createSignal<
    Record<string, ConflictResolution | undefined>
  >({});
  const [applying, setApplying] = createSignal(false);
  const [applyError, setApplyError] = createSignal<string | null>(null);

  const showingWarnings = (id: DecisionId) =>
    activeId() === id && conflictCheck.state() === "warnings";

  const allChosen = () => {
    const warnings = conflictCheck.warnings();
    return warnings.length > 0 && warnings.every((w) => choices()[w.id]);
  };

  const finishFlow = () => {
    setActiveId(null);
    setChoices({});
    conflictCheck.reset();
  };

  const startConfirm = async (id: DecisionId) => {
    setActiveId(id);
    setChoices({});
    setApplyError(null);
    const result = await conflictCheck.check({ decision_id: id });
    // Clear or unavailable — detection never blocks a confirm.
    if (result !== "warnings") {
      await options.onConfirm(id);
      finishFlow();
    }
  };

  const applyConfirm = async (id: DecisionId) => {
    setApplying(true);
    setApplyError(null);
    let confirmed = false;
    try {
      confirmed = await options.onConfirm(id);
      if (!confirmed) return;
      await conflictCheck.applyOutcomes(id, choices());
      finishFlow();
    } catch (err) {
      surfaceQuotaExhausted(err);
      setApplyError(describeAiError(err));
      // Once the confirm has landed the decision is no longer `proposed`, so
      // its row — the only control that could release `activeId` — has left
      // the queue. Holding the flow open would leave every remaining row's
      // Confirm disabled with no way back short of a reload, so a failure in
      // the edge/resolve bookkeeping ends the flow and is reported instead.
      // The warnings it did not reach stay pending, which is the honest
      // record: their consequence never landed.
      if (confirmed) finishFlow();
    } finally {
      setApplying(false);
    }
  };

  const confirm = (id: DecisionId) => {
    if (showingWarnings(id)) {
      if (allChosen()) void applyConfirm(id);
      return;
    }
    if (activeId() !== null) return;
    void startConfirm(id);
  };

  const cancel = () => {
    if (applying()) return;
    finishFlow();
  };

  const dismiss = (id: DecisionId) => {
    if (activeId() === id) finishFlow();
    options.onDismiss(id);
  };

  const confirmDisabled = (id: DecisionId): boolean => {
    if (activeId() === id) {
      if (applying()) return true;
      if (conflictCheck.state() === "checking") return true;
      if (conflictCheck.state() === "warnings") return !allChosen();
      return false;
    }
    // Only one check/apply flow runs at a time against the shared hook.
    return activeId() !== null;
  };

  return {
    warnings: conflictCheck.warnings,
    choices,
    applying,
    applyError,
    clearApplyError: () => setApplyError(null),
    showingWarnings,
    confirmDisabled,
    choose: (warningId, outcome) =>
      setChoices((prev) => ({ ...prev, [warningId]: outcome })),
    confirm,
    cancel,
    dismiss,
  };
}
